import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CircleCheckBig, CircleX } from 'lucide-react';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { ConfirmDialog, type ConfirmDialogVariant } from '@/components/common/ConfirmDialog';
import { FormSelectField } from '@/components/common/FormDialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { leaveTypesQueryOptions } from '@/features/leaveTypes/api/leaveTypes.api';
import {
  KEEP_PRESENT_REQUEST_OPTION_ID,
  LEAVE_REQUEST_TYPE_LABELS,
  REVIEW_COMMENT_MAX,
  REVIEW_DECISION,
} from '../constants/leaveApplication.constants';
import { useReviewLeaveApplication } from '../hooks/useReviewLeaveApplication';
import {
  reviewLeaveApplicationSchema,
  type LeaveRequestType,
  type ReviewDecision,
  type ReviewLeaveApplicationFormValues,
  type ReviewLeaveApplicationPayload,
} from '../types/leaveApplication.types';
import { getApproveAsName, getApproveAsOptions, toReviewPayload } from '../utils/approveAs';

const APPROVE_AS_REQUIRED_MESSAGE = 'Choose what to approve this request as';
const LOCKED_AFTER_APPROVE = 'Once approved, this request can no longer be changed.';
const LOCKED_AFTER_REJECT = 'Once rejected, this request can no longer be changed.';

interface ConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
  pendingLabel: string;
  variant: ConfirmDialogVariant;
}

function getConfirmCopy(
  decision: ReviewDecision,
  requestLabel: string,
  approveAsName: string | undefined,
): ConfirmCopy {
  const requestName = requestLabel.toLowerCase();
  if (decision === REVIEW_DECISION.REJECT) {
    return {
      title: `Reject this ${requestName}?`,
      description: LOCKED_AFTER_REJECT,
      confirmLabel: 'Reject',
      pendingLabel: 'Rejecting...',
      variant: 'destructive',
    };
  }
  return {
    title: `Approve this ${requestName}?`,
    description: approveAsName
      ? `It will be approved as ${approveAsName}. ${LOCKED_AFTER_APPROVE}`
      : LOCKED_AFTER_APPROVE,
    confirmLabel: 'Approve',
    pendingLabel: 'Approving...',
    variant: 'default',
  };
}

interface PendingReview {
  decision: ReviewDecision;
  payload: ReviewLeaveApplicationPayload;
  approveAsName: string | undefined;
}

interface LeaveApplicationReviewFormProps {
  applicationId: number;
  requestType: LeaveRequestType;
  disabled: boolean;
  onDirtyChange: (dirty: boolean) => void;
}

export function LeaveApplicationReviewForm({
  applicationId,
  requestType,
  disabled,
  onDirtyChange,
}: LeaveApplicationReviewFormProps) {
  const isPresentRequest = requestType === 'ATTENDANCE_CLAIM';
  const requestLabel = LEAVE_REQUEST_TYPE_LABELS[requestType];
  const form = useForm<ReviewLeaveApplicationFormValues>({
    resolver: zodResolver(reviewLeaveApplicationSchema),
    defaultValues: {
      comment: '',
      approveAs: isPresentRequest ? KEEP_PRESENT_REQUEST_OPTION_ID : undefined,
    },
  });
  const { data: leaveTypes, isLoading: isLeaveTypesLoading } = useQuery({
    ...leaveTypesQueryOptions(),
    enabled: isPresentRequest,
  });
  const approveAsOptions = useMemo(() => getApproveAsOptions(leaveTypes), [leaveTypes]);

  const [pendingReview, setPendingReview] = useState<PendingReview | null>(null);
  const reviewMutation = useReviewLeaveApplication(() => form.reset());
  const commentLength = form.watch('comment').length;
  const isSubmitting = reviewMutation.isPending || form.formState.isSubmitting;
  const { isDirty } = form.formState;

  useEffect(() => {
    onDirtyChange(isDirty);
    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  function requestReview(decision: ReviewDecision) {
    if (isSubmitting) return;
    void form.handleSubmit((values) => {
      const isApproving = decision === REVIEW_DECISION.APPROVE;
      if (isApproving && isPresentRequest && values.approveAs === undefined) {
        form.setError('approveAs', { message: APPROVE_AS_REQUIRED_MESSAGE });
        return;
      }
      setPendingReview({
        decision,
        payload: toReviewPayload(decision, values),
        approveAsName:
          isApproving && isPresentRequest
            ? getApproveAsName(approveAsOptions, values.approveAs)
            : undefined,
      });
    })();
  }

  function confirmReview() {
    if (!pendingReview) return;
    reviewMutation.mutate(
      {
        id: applicationId,
        decision: pendingReview.decision,
        payload: pendingReview.payload,
        requestLabel,
      },
      { onSettled: () => setPendingReview(null) },
    );
  }

  const confirmCopy = pendingReview
    ? getConfirmCopy(pendingReview.decision, requestLabel, pendingReview.approveAsName)
    : null;

  return (
    <Form {...form}>
      <div className="flex flex-1 flex-col gap-3">
        {isPresentRequest ? (
          <FormSelectField
            control={form.control}
            name="approveAs"
            label="Approve As"
            options={approveAsOptions}
            isLoading={isLeaveTypesLoading}
            placeholder="Choose a category"
          />
        ) : null}
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel className="gap-0.5">
                  Your Comment<span className="text-destructive">*</span>
                </FormLabel>
                <CharacterCounter current={commentLength} max={REVIEW_COMMENT_MAX} />
              </div>
              <FormControl>
                <Textarea
                  {...field}
                  maxLength={REVIEW_COMMENT_MAX}
                  placeholder="Enter your comment here..."
                  disabled={isSubmitting}
                  className="field-sizing-fixed min-h-40 resize-y overflow-y-auto"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="mt-auto flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="destructive"
            disabled={disabled || isSubmitting}
            onClick={() => requestReview(REVIEW_DECISION.REJECT)}
          >
            <CircleX />
            Reject
          </Button>
          <Button
            type="button"
            disabled={disabled || isSubmitting}
            onClick={() => requestReview(REVIEW_DECISION.APPROVE)}
          >
            <CircleCheckBig />
            Approve
          </Button>
        </div>
      </div>

      {confirmCopy && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next && !reviewMutation.isPending) setPendingReview(null);
          }}
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          pendingLabel={confirmCopy.pendingLabel}
          confirmVariant={confirmCopy.variant}
          isPending={reviewMutation.isPending}
          onConfirm={confirmReview}
        />
      )}
    </Form>
  );
}
